import ReactDOM from "react-dom/client";
import React, {PropsWithChildren, Suspense, useEffect, useState} from "react";
import App from "./App";
import { appTheme, colorSchemeManager } from "./theme";
import { LoadingOverlay, MantineProvider, useMantineColorScheme } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { SiteConfigProvider, useGraphqlUri } from "./context/SiteConfigContext";
import { SettingsProvider } from "./context/SettingsContext";
import { ActionsProvider } from "./context/ActionsContext";
import AppStartupGate from "@/components/bootstrap/BootstrapGate";

import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import './generated/tailwind.css'
import './sass/main.scss'
import './styles/overrides.css'
import {ApolloClient, ApolloProvider, createHttpLink, InMemoryCache} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import AuthProvider from 'react-auth-kit';
import createStore from "react-auth-kit/createStore";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader"
import createRefresh from "react-auth-kit/createRefresh";
import {RefreshDocument} from "./generated/graphql";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";

/**
 * The application bootstraps under a configuration-first gate. GraphQL and
 * other network clients must derive their URLs exclusively from
 * SiteConfigContext to ensure a single source of truth.
 */

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<RootBootstrap/>);

/**
 * RootBootstrap is the true app entry. It mounts the SiteConfigProvider and
 * gates the rest of the application behind AppStartupGate so that no
 * network-dependent providers initialize until configuration is ready.
 */
function RootBootstrap() {
    return (
        <MantineContext>
            <SiteConfigProvider>
                <AppStartupGate>
                    <AppContext/>
                </AppStartupGate>
            </SiteConfigProvider>
        </MantineContext>
    );
}

/**
 * Create an ApolloClient instance with a provided GraphQL URI.
 * Auth header is read dynamically from localStorage on each request to ensure
 * requests made immediately after login have the token even before React state updates.
 * The URI MUST come from SiteConfigContext to keep a single source of truth.
 */
function createApolloClient(uri: string, _authHeader: string | null) {
    const httpLink = createHttpLink({ uri });

    // Always use authLink - it reads token fresh from localStorage on each request
    // This ensures requests work immediately after login, even before React state propagates
    const authLink = setContext((_, { headers }) => {
        // Read token fresh from localStorage on EACH request
        const token = typeof window !== 'undefined' ? localStorage.getItem('_auth') : null;
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : "",
            },
        };
    });

    return new ApolloClient({
        link: authLink.concat(httpLink),
        cache: new InMemoryCache(),
    });
}



/**
 * AppContext composes all providers that must initialize only after
 * site configuration is ready (Auth, Apollo, Mantine, etc.).
 */
function AppContext() {
    // Resolve GraphQL URI from SiteConfigContext (single source of truth)
    const resolvedGraphqlUri = useGraphqlUri(); // guaranteed non-null after startup gate

    // Initialize refresh only after bootstrap readiness (inside gate)
    const refresh = React.useMemo(() => createRefresh<any>({
        interval: 300, // seconds (5 minutes)
        // @ts-ignore
        refreshApiCallback: async (param) => {
            try {
                // Derive API base from GraphQL URI origin
                const apiBase = new URL(resolvedGraphqlUri!).origin;
                const res = await fetch(`${apiBase}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ refreshToken: param.refreshToken })
                });
                if (!res.ok) return { isSuccess: false };
                const data = await res.json().catch(() => null) as { isAuthenticated?: boolean; token?: string; refreshToken?: string } | null;
                if (!data || !data.isAuthenticated) return { isSuccess: false };
                return {
                    isSuccess: true,
                    newAuthToken: data.token ?? '',
                    newRefreshToken: data.refreshToken ?? '',
                    newAuthTokenExpireIn: 720,
                    newRefreshTokenExpiresIn: 720
                };
            } catch (error) {
                console.error('Refresh error', error);
                return { isSuccess: false };
            }
        }
    }), [resolvedGraphqlUri]);

    const ctxStore = React.useMemo(() =>
        createStore({
            authName: '_auth',
            authType: 'localstorage',
            refresh: refresh
        })
    , [refresh]);

    return (
        <AuthProvider store={ctxStore}>
            <ApolloContext>
                <App />
            </ApolloContext>
        </AuthProvider>
    );
}



/**
 * ApolloContext provides a configured ApolloProvider. It rebuilds the client
 * when the auth header or auth state changes so requests include credentials.
 */
function ApolloContext({children}: PropsWithChildren) {
    const authHeader = useAuthHeader(); // Hook to get the Bearer token
    const isAuthenticated = useIsAuthenticated();

    // Resolve URI from SiteConfigContext
    const resolvedGraphqlUri = useGraphqlUri(); // guaranteed non-null after startup gate

    const client = React.useMemo(
        () => createApolloClient(resolvedGraphqlUri!, authHeader),
        [resolvedGraphqlUri, authHeader, isAuthenticated]
    );

    return <ApolloProvider client={client}>
        <ActionsProvider>
            <SettingsProvider>
                {children}
            </SettingsProvider>
        </ActionsProvider>
    </ApolloProvider>
}

/**
 * MantineContext wraps the UI theme provider and Suspense fallback used
 * throughout the app. Placed after the startup gate so that UI mounts only
 * when the application is ready.
 */
function MantineContext({children}: PropsWithChildren) {
    // Centralized theme and color scheme persistence live in src/theme.ts
    // - To change brand colors or component defaults, edit appTheme in src/theme.ts
    // - To change how a color scheme is stored (localStorage/cookie), edit colorSchemeManager
    return (
        <MantineProvider theme={appTheme} colorSchemeManager={colorSchemeManager} defaultColorScheme="auto" withGlobalClasses>
            {/* Sync Mantine color scheme with Tailwind's class-based dark mode */}
            <SyncTailwindDarkMode />
            <Notifications />
            <Suspense fallback={<LoadingOverlay visible={true}/>}>
                {children}
            </Suspense>
        </MantineProvider>
    );
}

/**
 * SyncTailwindDarkMode
 * Ensures Tailwind's `dark` class on <html> matches Mantine's color scheme.
 * This lets us use Tailwind `dark:` utilities while keeping Mantine as the
 * source of truth for theme selection and persistence.
 */
function SyncTailwindDarkMode() {
    const { colorScheme } = useMantineColorScheme();
    React.useEffect(() => {
        const root = document.documentElement;
        if (colorScheme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [colorScheme]);
    return null;
}
